import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { ContentContainer } from 'components/ui/ContentContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faUser, faTag } from '@fortawesome/pro-light-svg-icons';
import useAppDispatch from 'hooks/useAppDispatch';
import { clearCustomerSearch, clearOrderSearchFilters, clearSearchOrders } from 'store/reducers/orderSlice';
import { CustomerSearchItem, ReturnCenterDisplaySettingsResponse } from 'returnCenter-api';
import CustomerSearchStep from './CustomerSearchStep';
import ArticleSearchStep from './ArticleSearchStep';
import useAppSelector from 'hooks/useAppSelector';
import { companiesSelector } from 'store/reducers/companiesSlice';
import OrderDateFilter from 'components/Filters/OrderDateFilter';

type SearchStep = 'customer' | 'articles';

const CustomerOrderSearchByArticle = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const siteSettings = useAppSelector(companiesSelector.selectDisplaySettings);

    const [step, setStep] = useState<SearchStep>('customer');
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchItem | null>(null);

    useEffect(() => {
        return () => {
            dispatch(clearCustomerSearch());
            dispatch(clearSearchOrders());
            dispatch(clearOrderSearchFilters());
        };
    }, [dispatch]);

    const handleCustomerSelect = useCallback(
        (customer: CustomerSearchItem) => {
            setSelectedCustomer(customer);
            setStep('articles');
            dispatch(clearSearchOrders());
        },
        [dispatch],
    );

    const handleBack = useCallback(() => {
        setStep('customer');
        setSelectedCustomer(null);
        dispatch(clearSearchOrders());
    }, [dispatch]);

    return (
        <ContentContainer align="center" className="home-customer-search" $grow={1} column>
            <SearchWrapper column $grow={1}>
                <Heading as="h2" fontSize={32} lineheight={20}>
                    {t('searchCustomer')}
                </Heading>

                <StepIndicator justify="between" align="center">
                    <Flex align="center" gap="1rem">
                        <StepLabel
                            $active={step === 'customer'}
                            $completed={step === 'articles'}
                            siteSettings={siteSettings}
                            onClick={step === 'articles' ? handleBack : undefined}
                        >
                            <FontAwesomeIcon icon={faUser} />
                            <span>1. {t('customer')}</span>
                        </StepLabel>
                        <StepArrow icon={faChevronRight} />
                        <StepLabel $active={step === 'articles'} siteSettings={siteSettings}>
                            <FontAwesomeIcon icon={faTag} />
                            <span>2. {t('searchByArticle')}</span>
                        </StepLabel>
                    </Flex>
                    {step === 'articles' && <OrderDateFilter />}
                </StepIndicator>

                {/* Selected customer breadcrumb */}
                {selectedCustomer && step === 'articles' && (
                    <SelectedCustomerBar align="center" gap="0.5rem">
                        <FontAwesomeIcon icon={faUser} />
                        <Text fontSize={14} margin={0}>
                            <strong>{selectedCustomer.name}</strong> ({selectedCustomer.customerCode})
                        </Text>
                    </SelectedCustomerBar>
                )}

                {/* Step content */}
                {step === 'customer' && <CustomerSearchStep onCustomerSelect={handleCustomerSelect} />}
                {step === 'articles' && selectedCustomer?.customerCode && (
                    <ArticleSearchStep customerCode={selectedCustomer.customerCode} />
                )}
            </SearchWrapper>
        </ContentContainer>
    );
};

export default CustomerOrderSearchByArticle;

// --- Styled components ---

const SearchWrapper = styled(Flex)`
    width: 100%;
`;

const Heading = styled(Text)`
    padding: 0 !important;
`;

const StepIndicator = styled(Flex)`
    padding: 0.25rem 0;
    width: 100%;
`;

const StepLabel = styled.span<{
    $active?: boolean;
    $completed?: boolean;
    siteSettings?: ReturnCenterDisplaySettingsResponse | null;
}>`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding-bottom: 0.35rem;
    font-size: 14px;
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
    color: ${({ $active, $completed, theme, siteSettings }) =>
        $active
            ? siteSettings?.accentThemeColor ?? theme.colors.accent
            : $completed
            ? theme.colors.text
            : theme.colors.textFaded};
    border-bottom: 2px solid
        ${({ $active, theme, siteSettings }) =>
            $active ? siteSettings?.accentThemeColor ?? theme.colors.accent : 'transparent'};
    cursor: ${({ $completed }) => ($completed ? 'pointer' : 'default')};
    transition: all 0.2s ease;

    &:hover {
        color: ${({ $completed, theme, siteSettings }) =>
            $completed ? siteSettings?.accentThemeColor ?? theme.colors.accent : undefined};
    }
`;

const StepArrow = styled(FontAwesomeIcon)`
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textFaded};
`;

const SelectedCustomerBar = styled(Flex)`
    padding: 0.5rem 0.75rem;
    background: ${({ theme }) => theme.colors.fillLight};
    border-radius: var(--radius);
    margin-top: 0.5rem;
    color: ${({ theme }) => theme.colors.text};
    font-size: 13px;
`;
